import React, { useEffect, useState } from "react";
import Layout from "../../components/layout";
import { FaArrowLeft, FaPlus, FaSearch, FaCalendarAlt } from "react-icons/fa";

import styles from "./PurchasePage.module.scss";
import { useNavigate } from "react-router-dom";
import { route } from "../../static/route";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchPurchaseListData,
  getPurchases,
  getTotalPurchases,
  getPartyOptions,
  fetchPurchasePartiesOptions,
} from "../../features/purchase/purchaseSlice";
import PurchaseList from "../../components/purchase/PurchaseList";
import Pagination from "../../components/pagination";
import { toast } from "react-toastify";
import { toastStyles, selectStyles } from "../../static/styles";
import { DELETE } from "../../services/axios.service";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";

const PurchasePage = () => {
  const companyName = process.env.REACT_APP_COMPANY_NAME;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const purchases = useAppSelector(getPurchases);
  const totalPurchases = useAppSelector(getTotalPurchases);
  const partyOptions = useAppSelector(getPartyOptions);

  interface FilterFormInput {
    search: string;
    fromDate: string;
    toDate: string;
    party_id: number;
  }

  const { handleSubmit, register, reset, getValues, control, setValue } =
    useForm<FilterFormInput>();

  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(20);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showFilter, setShowFilter] = useState(false);

  const hanldeGoToHome = () => {
    navigate(route.dashboard);
  };

  const paginate = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handleDeletePurchase = async (id: number, password: string) => {
    try {
      await DELETE(`/v1/admin/purchases/${id}`, { password })
        .then(async (response: any) => {
          if (response.data.status === "success") {
            const pageNumber =
              purchases.length > 1 ? page : page - 1;
            if (page === pageNumber) {
              fetchPurchasesList(page, pageSize);
            } else {
              setPage(pageNumber);
            }

            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }
  };

  const handleOnFilterSubmit = async () => {
    fetchPurchasesList(page, pageSize);
  };

  const handleClearFilter = () => {
    reset();
    fetchPurchasesList(page, pageSize);
  };

  const fetchPurchasesList = async (page: number, size: number) => {
    const data = getValues();

    setIsLoading(true);

    dispatch(
      fetchPurchaseListData({
        page: page,
        size: size,
        search: data?.search || null,
        fromDate: data?.fromDate || null,
        toDate: data?.toDate || null,
        party_id: data?.party_id || null,
      })
    ).then(() => {
      setIsLoading(false);
    });
  };

  useEffect(() => {
    dispatch(fetchPurchasePartiesOptions());
  }, []);

  useEffect(() => {
    fetchPurchasesList(page, pageSize);
  }, [page]);

  return (
    <Layout>
      <div className={styles.PurchasePage}>
        <div className="logoSection">
          <h1
            onClick={hanldeGoToHome}
            className="text-4xl font-bold text-white lg:text-2xl cursor-pointer lg:w-fit"
          >
            {companyName}
          </h1>

          <hr className="w-[180px] border-white border-[4px] rounded-[4px] mx-auto mt-4 lg:mx-0 lg:w-24 lg:border-[2px] lg:mt-2" />
        </div>

        <div className="pageTitleSection">
          <FaArrowLeft
            onClick={hanldeGoToHome}
            className="lg:hidden absolute top-5 left-5 text-3xl text-white border-2 border-white p-1 rounded-full"
          />

          <h1 className="text-5xl mt-6 lg:mt-0 font-bold text-white lg:text-2xl lg:text-primary-1">
            ખરીદી ની વિગતો
          </h1>
        </div>

        <div className="mt-[60px] px-10 py-5">
          <div className="flex justify-end gap-4 items-center mb-4">
            <button
              className="p-2 rounded-md flex items-center bg-gradient-to-r from-primary-1 to-primary-2 text-white transition-colors duration-300"
              onClick={() => setShowFilter(!showFilter)}
            >
              <FaSearch className="mr-2" />{" "}
              {showFilter ? "ફિલ્ટર છુપાવો" : "ફિલ્ટર બતાવો"}
            </button>

            <button
              className="bg-gradient-to-r from-primary-1 to-primary-2 text-white p-2 rounded-md flex items-center transition-colors duration-300"
              onClick={() => navigate(route.purchaseCreate)}
            >
              <FaPlus className="mr-2" /> નવી ખરીદી ઉમેરો
            </button>
          </div>

          {showFilter && (
            <form onSubmit={handleSubmit(handleOnFilterSubmit)}>
              <div className="mb-4 bg-white shadow rounded p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded pr-[2px] h-[44px]">
                    <FaCalendarAlt className="text-white mx-2" />
                    <input
                      className="bg-white text-primary-1 placeholder:text-primary-2 font-semibold text-xl focus:outline-none p-2 rounded flex-grow h-[40px]"
                      type="date"
                      {...register("fromDate")}
                      placeholder="From Date"
                    />
                  </div>

                  <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded pr-[2px] h-[44px]">
                    <FaCalendarAlt className="text-white mx-2" />
                    <input
                      className="bg-white text-primary-1 placeholder:text-primary-2 font-semibold text-xl focus:outline-none p-2 rounded flex-grow h-[40px]"
                      type="date"
                      {...register("toDate")}
                      placeholder="To Date"
                    />
                  </div>

                  <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded p-[2px] h-[44px]">
                    <input
                      {...register("search")}
                      className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 w-full h-full rounded focus:outline-none"
                      placeholder="ID / વાઉચર નં. દ્વારા શોધો"
                    />
                  </div>

                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-12 w-full relative rounded">
                    <Controller
                      {...register("party_id")}
                      control={control}
                      render={({ field }) => (
                        <Select
                          isClearable
                          {...field}
                          value={
                            partyOptions.find(
                              (val) => val.value === field.value
                            ) || null
                          }
                          className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          styles={selectStyles}
                          placeholder={"પાર્ટી નું નામ સિલેક્ટ કરો"}
                          options={partyOptions}
                          onChange={(val: any) =>
                            setValue("party_id", val?.value)
                          }
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="flex gap-4 justify-end mt-4">
                  <button
                    onClick={handleClearFilter}
                    type="button"
                    className="bg-primary-1 hover:bg-primary-2 text-white px-4 py-2 rounded-md transition duration-300 ease-in-out"
                  >
                    હટાઓ
                  </button>

                  <button
                    type="submit"
                    className="bg-primary-1 hover:bg-primary-2 text-white px-4 py-2 rounded-md transition duration-300 ease-in-out"
                  >
                    ફિલ્ટર લાગુ કરો
                  </button>
                </div>
              </div>
            </form>
          )}

          <PurchaseList
            purchases={purchases}
            pageNumber={page}
            pageSize={pageSize}
            isLoading={isLoading}
            handleDeletePurchase={handleDeletePurchase}
          />

          <Pagination
            itemsPerPage={pageSize}
            totalItems={totalPurchases}
            paginate={paginate}
            currentPage={page}
          />
        </div>
      </div>
    </Layout>
  );
};
export default PurchasePage;
